import React, { useState, useEffect, useRef } from 'react';
import { useBlocker } from '@tanstack/react-router';
import { FormikConsumer, FormikActions } from 'formik';
import {
  ModalView,
  Stack,
  Button,
  Text,
  withTranslation,
} from '@piwikpro/ui-components';

export type ModalTypes = 'twoOptions' | 'threeOptions';

interface RouterPromptProps {
  type: ModalTypes
  onSave: FormikActions<any>['submitForm']
  onDiscard: FormikActions<any>['resetForm']
  isModified: boolean
  promptTriggered: boolean
  updatePromptStatus: (status: boolean) => void
  forceDisableNativePrompt: boolean
  t: (key: string, variables?: any) => string
}

const RouterPrompt: React.FC<RouterPromptProps> = ({
  isModified, onDiscard, onSave, type, promptTriggered, updatePromptStatus,
  forceDisableNativePrompt, t,
}) => {
  // `onDiscard` (e.g. AddUsers.handleClose) may itself navigate away - that navigation
  // must not be blocked again by this same blocker, otherwise discarding never completes.
  const isDiscardingRef = useRef(false);
  const { proceed, reset, status } = useBlocker({
    shouldBlockFn: () => isModified && !isDiscardingRef.current,
    withResolver: true,
    // Boolean `true` always fires the native beforeunload dialog (refresh / leave
    // the module). Gate it on dirty state the same way as in-app navigation.
    enableBeforeUnload: !forceDisableNativePrompt && isModified,
  });

  useEffect(() => {
    if (!forceDisableNativePrompt && isModified) {
      window.onbeforeunload = () => true;
    } else {
      window.onbeforeunload = null;
    }
    return () => { window.onbeforeunload = null; };
  }, [isModified, forceDisableNativePrompt]);

  useEffect(() => {
    if (!isModified && promptTriggered) {
      onDiscard();
      updatePromptStatus(false);
    }
  }, [isModified, promptTriggered]);

  const handleKeepEditing = () => {
    reset?.();
    updatePromptStatus(false);
  };

  const handleDiscard = async () => {
    isDiscardingRef.current = true;
    try {
      await onDiscard();
    } finally {
      isDiscardingRef.current = false;
    }
    updatePromptStatus(false);
    proceed?.();
  };

  const handleSave = async () => {
    await onSave();
    updatePromptStatus(false);
    proceed?.();
  };

  const isBlocked = status === 'blocked';

  if (isModified && (isBlocked || promptTriggered)) {
    if (type === 'twoOptions') {
      return (
        <ModalView
          title={t('form.unsavedChangesTracker.twoOptions.modal.title')}
          actions={(
            <Stack spacing="narrow" horizontalAlignment="right" verticalAlignment="center">
              <Button text={t('form.unsavedChangesTracker.twoOptions.modal.keep-editing')} onClick={handleKeepEditing} />
              <Button appearance="destructive" text={t('form.unsavedChangesTracker.twoOptions.modal.discard')} onClick={handleDiscard} />
            </Stack>
          )}
        >
          <Text as="p">
            {t('form.unsavedChangesTracker.twoOptions.modal.body')}
          </Text>
        </ModalView>
      );
    }
    return (
      <ModalView
        title={t('form.unsavedChangesTracker.threeOptions.modal.title')}
        actions={(
          <Stack spacing="narrow" horizontalAlignment="right" verticalAlignment="center">
            <Stack.Item fill>
              <Button text={t('form.unsavedChangesTracker.threeOptions.modal.dont-save')} onClick={handleDiscard} />
            </Stack.Item>
            <Stack.Item>
              <Stack spacing="narrow">
                <Stack.Item>
                  <Button text={t('form.unsavedChangesTracker.threeOptions.modal.keep-editing')} onClick={handleKeepEditing} />
                </Stack.Item>
                <Stack.Item>
                  <Button appearance="primary" text={t('form.unsavedChangesTracker.threeOptions.modal.save')} onClick={handleSave} />
                </Stack.Item>
              </Stack>
            </Stack.Item>
          </Stack>
        )}
      >
        <Text as="p">
          {t('form.unsavedChangesTracker.threeOptions.modal.body')}
        </Text>
      </ModalView>
    );
  }
  return null;
};

interface UnsavedChangesTrackerProps {
  type: ModalTypes
  render: (api: {
    UnsavedChangesModal: React.ElementType
    onClose: () => void
    forceDisableNativePrompt: () => void
  }) => React.ReactElement
  discardAction?: () => void
  onSave?: () => void
  isModified?: boolean
  t: (key: string, variables?: any) => string
}

const UnsavedChangesTracker: React.FC<UnsavedChangesTrackerProps> = ({
  render, type, discardAction, onSave, isModified, t,
}) => {
  const [promptTriggered, setPromptTriggered] = useState(false);
  const [forceDisabledNativePrompt, setForceDisabledNativePrompt] = useState(false);

  const UnsavedChangesModal = () => (
    <FormikConsumer>
      {(formik) => {
        const isFormModified = formik.dirty && !formik.isSubmitting;
        return (
          <RouterPrompt
            t={t}
            promptTriggered={promptTriggered}
            updatePromptStatus={setPromptTriggered}
            type={type}
            forceDisableNativePrompt={forceDisabledNativePrompt}
            isModified={isModified !== undefined ? isModified : isFormModified}
            onDiscard={discardAction || (() => formik.resetForm())}
            onSave={onSave || (() => formik.submitForm())}
          />
        );
      }}
    </FormikConsumer>
  );

  return render({
    UnsavedChangesModal,
    onClose: () => setPromptTriggered(true),
    forceDisableNativePrompt: () => setForceDisabledNativePrompt(true),
  });
};

export default withTranslation('platform')(UnsavedChangesTracker);
